import { Component, EventEmitter, Injectable, Input, OnInit, Output } from "@angular/core";
import { Message, MessageService } from "primeng/api";

@Injectable({
	providedIn: "root"
})

@Component({
	selector: "message-master",
	template: `
		<p-messages
			key="{{ _key }}">
		</p-messages>
	`
})


/**
 * A master message messaging service that helps facilitate status changes.
 * Note: CANNOT coexist with the MessageService provider in implemented component.
 * MUST NOT be inside of an ngIf as the component will initialize and not register messages
 * after the page renders.
 * 
 * Additional Documentation: https://chapls.atlassian.net/wiki/spaces/KB/pages/2168291329/Toast+and+Message+Master+Structure
 *
 * Example: <message-master
 *				position="center"
 *				key="exampleKey"
 *				dialog="true"
 *				(onClose)="onReject('exampleKey')"
 *				(onConfirm)="onConfirm(someFormGroup.value)"
 *				[modal]="true"
 *				[baseZIndex]="15000"></message-master>
 *
 * @author			John Ying <john.ying@childrenshospitals.org>
 * @created			5/22/2023
 * @modifiedBy		John Ying <john.ying@childrenshospitals.org>
 * @modifiedDate	6/22/2023
 */
export class MessageMaster implements OnInit {
	
	messages: Message[] = [];

	/**
	 * Set the message key
	 */
	@Input() set key(value: string) {
		this._key = value;
	}

	/**
	 * Set the message position
	 */
	@Input() set position(value: string) {
		this._position = value;
	}

	/**
	 * Render a confirm dialog that emits to onClose and onConfirm
	 */
	@Input() dialog = false;

	/**
	 * Event called when the dialog is closed and when the user selects No when dialog is true
	 */
	@Output() onClose: EventEmitter<boolean> = new EventEmitter();

	/**
	 * Event called when the user selects Yes when dialog is true
	 */
	@Output() onConfirm: EventEmitter<boolean> = new EventEmitter();

	/**
	 * The primeng dialog modal option
	 */
	@Input() modal = false;

	/**
	 * Set the base zIndex value to use in layering
	 */
	@Input() baseZIndex = 0;

	constructor(private messageService: MessageService) {}

	private _position: string; //this is not bound after initialization (using it after ngOnInit will not work)
	private _key: string;

	ngOnInit(): void {
		if (this._position === undefined) {
			this._position = "top-right";
		} else if (this._position === "top-right") {
			console.error("messageMaster must NOT have position top-right. It is reserved.\n Use a different location or omit it.");
		}

		if (this._key === undefined) {
			this._key = "message-master";
		} else if (this._key === "message-master") {
			console.error("message-master is a reserved key.\n Use a different key or omit it from the html attribute.");
		}
	}

	/**
	 * Clears the entire message service log.
	 */
	public clear(): void {
		this.messageService.clear();
	}

	/**
	 * Clears the message log for a specified key. Default: "message-master"
	 *
	 * @param key    - the unique identifier key of the message service to clear
	 */
	public clearKey(key = "message-master"): void {
		this.messageService.clear(key);
	}

	/**
	 * Prints a global message.
	 * Example: {severity: "success", summary: "Success", detail: "Report has been created"}
	 *
	 * @param message    - the message to print as message
	 */
	public message(message: Message): void {
		message["key"] = "message-master";
		this.messageService.add(message);
	}

	/**
	 * Prints a unique p-message message that allows for custom parameters.
	 * Note: Cannot be called in ngOnInit unless delayed (through a subscribe or setTimeout).
	 *
	 * @param key 		- the unique identifier key. Must NOT be "message-master".
	 * @param message	- the {@link Message} message to print as message. Example: {severity: "success", summary: "My Report", detail: "Report has been created"}
	 * @param dialog	- include an optional confirm dialog. Default: false
	 */
	public uniqueMessage(key: string, message: Message, dialog = false): void {
		this.dialog = dialog;
		if (key.toLowerCase() !== "message-master") {
			message["key"] = key;
			this.messageService.add(message);
		} else {
			console.error("message-master is a reserved key.\n Use the message function instead.");
		}
	}

	/**
	 * Emits the onClose event
	 */
	private onmessageClose(): void {
		this.onClose.emit(true);
	}

	/**
	 * Emits the onConfirm event
	 */
	private onmessageConfirm(): void {
		this.onConfirm.emit(true);
	}
}
